import React, { useRef } from 'react';

//Hooks
import {useEffect} from 'react';
import { Redirect, useHistory, useParams } from 'react-router-dom';
import {useSelector, useDispatch} from "react-redux";
import {useTranslation} from "react-i18next";

//Actions
import * as actions from '../../../store/actions';

//Components
import {Button, Container, Row} from 'react-bootstrap';
import {Calendar, Clock, Users, MapPin, Phone} from "react-feather";
import Col from "../../../components/Col/Col";
import ScreenHeader from "../../../components/ScreenHeader/ScreenHeader";
import ScreenFooter from "../../../components/ScreenFooter/ScreenFooter";
import WebVideo from "../../../components/WebVideo/WebVideo";
import Loader from "react-loader-spinner";
import GoogleMapReact from 'google-map-react';
import google_map_input from '../../../services/settings';

//Styles
import Styles from './Event.module.scss';

//Utils
import moment from 'moment';

import { ROUTER_URL } from '../../../const/ROUTER_URL';

import { useBool } from '../../../hooks/useBool';
import { useIsFirstRender } from '../../../hooks/useIsFirstRender';
import { formatPrice } from '../../../helpers/function/format';
import { useFormatedText } from '../../../hooks/useFormattedText';

const extractEventDetails = (event) => ({
    video_url: event.video_url,
    description: event.description,
    photo_url: event.photo_url,
    contact_number: event.contact_number,
    street: event.street,
    city: event.city,
    zip: event.zip,
    country: event.country,
    latitude: event.latitude,
    longitude: event.longitude,
    hide_price: event.hide_price,
});

const Marker = () => (
    <div className={Styles.markerContainer}>
        <div className={Styles.marker}/>
    </div>
);

const Event = () => {
    const { t } = useTranslation('common');
    const { shopId, eventId } = useParams();
    const shopLoadingRef = useRef(false);
    const shopLoaded = useBool(false);
    const eventsLoadingRef = useRef(false);
    const eventsLoaded = useBool(false);
    const { isFirstRender } = useIsFirstRender();
    const eventsLoading = useSelector(state => state.events.loading);
    const selectedEvent = useSelector(state => {
        let selectedEvent = {};
        state.events.events?.some((event) => {
            if (event.serviceSubOptions.length) {
                return event.serviceSubOptions.some((subOption) => {
                    const isSelected = (
                        String(subOption.id) === String(eventId)
                        || String(subOption.serviceId) === String(eventId)
                    );
    
                    if (isSelected) {
                        selectedEvent = {
                            ...subOption,
                            ...extractEventDetails(event),
                        };
                    }
    
                    return isSelected;
                })
            } else {
                const isSelected = (
                    String(event.id) === String(eventId)
                    || String(event.serviceId) === String(eventId)
                );
                if (isSelected) {
                    selectedEvent = event;
                }
                return isSelected;
            }
        })

        return selectedEvent

    });
    const { shopLoading } = useSelector(state => state.companies);

    const style = useSelector(state => state.widget.style);
    const dispatch = useDispatch();

    useEffect(() => {
        dispatch(actions.selectService(null));
        dispatch(actions.selectGroup(null));
        dispatch(actions.selectBundle(null));
    }, [dispatch]);

    useEffect(() => {
        if(!shopId || isNaN(shopId)) {
            return;
        }
        shopLoadingRef.current = true;
        dispatch(actions.getShop(shopId));
    }, [dispatch, shopId]);

    useEffect(() => {
        if (isFirstRender) {
            return;
        }
        if (!shopLoading && shopLoadingRef.current) {
            shopLoadingRef.current = false;
            shopLoaded.onTrue();
            eventsLoadingRef.current = true;
            // TODO: we need to fix this and load only the event for the selected shop, not all events
            dispatch(actions.getEvents(0, 100));
        }
    }, [isFirstRender, shopLoading, shopLoaded, dispatch]);

    useEffect(() => {
        if (!shopLoaded.value) {
            return;
        }
        if (!eventsLoading && eventsLoadingRef.current) {
            eventsLoadingRef.current = false;
            eventsLoaded.onTrue();
        }
    }, [shopLoaded.value, eventsLoading, eventsLoaded]);

    const { push } = useHistory();
    const {
        video_url: videoURL,
        show_sessions_left: showSessionLeft,
        photo_url: photoURL,
        groupName: name,
        serviceName: subName,
        cost: price,
        time,
        total,
        available,
        description,
        latitude,
        longitude,
        street,
        zip,
        city, 
        country,
        contact_number: phoneNumber,
        hide_price: isHidePrice,
    } = selectedEvent || {};
    const isEmptyEvent = !Object.keys(selectedEvent).length;
    const isEventLoading = isFirstRender || shopLoading || shopLoadingRef.current || eventsLoading || eventsLoadingRef.current;
    const isShowPrice = !(isHidePrice === true && Number(price) === 0);

    const preparedDescription = useFormatedText(description);

    if (shopLoaded.value && eventsLoaded.value && !selectedEvent) {
        return (
            <Redirect to={ROUTER_URL.SHOPS_ID_EVENTS({ shopId })} />
        );
    }

    if (isEventLoading) {
        return (
            <div className={Styles.centeredContainer}>
                <Loader
                    type="TailSpin"
                    color="#000"
                    height={48}
                    width={48}
                />
            </div>
        );
    }

    if (isEmptyEvent) {
        return (
            <div className={Styles.centeredContainer}>
                <h2 className={Styles.title}>
                    {t('eventDetailScreen.eventNotFound')}
                </h2>
            </div>
        );
    }


    return (
        <>
            <ScreenHeader>
                <h2 className={Styles.title} data-testid='data-test-event-title'>
                    {name}
                </h2>
            </ScreenHeader>

            <div
                className={[
                    Styles.container,
                    style !== 'side' ? 'pt-md-4' : undefined,
                ].join(' ')}
                style={{paddingBottom:  (style === 'dev' || style === 'website')  ? '130px' : '30px'}}
            >
                <Container>
                    <Row>
                        <Col md={6} className={`mt-0 mt-md-4`}>
                            {videoURL ?
                                (
                                    <WebVideo className={[
                                        Styles.photo,
                                        style === 'side' ? Styles.side : undefined
                                    ].join(' ')} url={videoURL}/>
                                ) : (
                                    <img className={[
                                        Styles.photo,
                                        style === 'side' ? Styles.side : undefined
                                    ].join(' ')} src={photoURL} alt={name}/>
                                )}

                            <h1 className={Styles.name} data-testid='data-test-event-name'>
                                {subName}
                            </h1>

                            {isShowPrice && (
                                <span className={Styles.price} data-testid='data-test-event-price'>
                                    {formatPrice(price)}
                                </span>
                            )}

                            <div className={'d-flex flex-column'}>
                                <div className={'d-flex flex-row align-items-center'}>
                                    <Calendar size={18} className={[
                                        Styles.detailIcon,
                                        'mr-3'
                                    ].join(' ')}/>

                                    <span className={'w-25'}>
                                        {`${t('reservationScreen.date')}:`}
                                    </span>

                                    <span className={'font-weight-bold'} data-testid='data-test-event-date'>
                                        {moment.unix(time).utc().format('ddd D MMMM YYYY')}
                                    </span>
                                </div>

                                <div className={'d-flex flex-row align-items-center mt-2'}>
                                    <Clock size={18} className={[
                                        Styles.detailIcon,
                                        'mr-3'
                                    ].join(' ')}/>

                                    <span className={'w-25'}>
                                        {t('eventDetailScreen.startTime')}
                                    </span>

                                    <span className={'font-weight-bold'} data-testid='data-test-event-time'>
                                        {moment(time * 1000).utc().format('HH:mm')}
                                    </span>
                                </div>
                                {
                                    showSessionLeft && (
                                        <div className={'d-flex flex-row align-items-center mt-2'}>
                                            <Users size={18} className={[
                                                Styles.detailIcon,
                                                'mr-3'
                                            ].join(' ')}/>

                                            <span className={'w-25'}>
                                                {t('eventDetailScreen.quantity')}
                                            </span>

                                            <span className={'font-weight-bold'} data-testid='data-test-event-quantity'>
                                                {`${total - available}/${total}`}
                                            </span>
                                        </div>
                                    )
                                }

                            </div>
                        </Col>

                        <Col md={6} className={'mt-4'}>
                            <span className={Styles.label}>
                                {t('eventDetailScreen.aboutEvent')}
                            </span>

                            <p className={Styles.description} data-testid='data-test-event-description'>
                                {preparedDescription}
                            </p>

                            <div className={Styles.horizontalLine}/>

                            <div className={Styles.map}>
                                <GoogleMapReact
                                    bootstrapURLKeys={{key: google_map_input}}
                                    defaultCenter={{
                                        lat: latitude,
                                        lng: longitude
                                    }}
                                    defaultZoom={11}
                                >
                                    <Marker lat={latitude} lng={longitude}/>
                                </GoogleMapReact>
                            </div>

                            <div className={'d-flex flex-column mt-4'}>
                                <div className={'d-flex flex-row mt-2'}>
                                    <MapPin className={'text-muted mr-3 mt-1'} size={18}/>

                                    <div>
                                        <span className={'d-block font-weight-bold'}
                                              style={{fontSize: 17}}>{street}</span>
                                        <span className={'d-block'} style={{fontSize: 17}}>{`${zip} ${city}`}</span>
                                        <span className={'d-block'} style={{fontSize: 17}}>{` ${country}`}</span>
                                    </div>
                                </div>

                                <div className={'d-flex flex-row mt-4'}>
                                    <Phone className={'text-muted mr-3 mt-1'} size={18}/>

                                    <div>
                                        <span className={'d-block font-weight-bold'} style={{fontSize: 17}} data-testid='data-test-event-phone'>
                                            {phoneNumber}
                                        </span>
                                    </div>
                                </div>
                            </div>
                        </Col>
                    </Row>
                </Container>
            </div>

            <ScreenFooter visible={true}>
                <Button
                    variant={'tint'}
                    size={'lg'}
                    className={Styles.bookButton}
                    onClick={() => {
                        dispatch(actions.selectEvent(eventId));
                        push(ROUTER_URL.BOOK_CONFIRM_EVENT);
                    }}
                    data-testid='data-test-event-book-button'
                >
                    {t('homeScreen.makeABooking')}
                </Button>
            </ScreenFooter>
        </>
    );
};

export default Event
